// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import * as tracker from 'presentations/views/helpers/analytics';
import { spacing, mediaMax, fontSize } from 'presentations/views/styles';
import Icon from 'presentations/views/components/atoms/Icon';
import Button from 'presentations/views/components/atoms/Button';
import { eventPath, signUpPath, webPath } from 'presentations/routes/path';
import { SharedEventDetailItem } from 'presentations/api/schema/event';
import { gray05, white } from 'presentations/views/styles/color';
import Headline from 'presentations/views/components/atoms/Headline';
import { ViewerItem } from 'presentations/api/schema/user';
import { formatToISO, getDateTime } from 'infras/datetime';
import { formatLocationNameByEvent } from 'presentations/views/helpers/formatter';

interface Props {
  viewer: ViewerItem | null;
  event: SharedEventDetailItem;
}

function googleCalendarLink(event: SharedEventDetailItem) {
  // ref: https://github.com/InteractionDesignFoundation/add-event-to-calendar-docs/blob/main/services/google.md
  const t = translateInComponent(translations);
  const url = new URL('https://www.google.com/calendar/render');
  url.searchParams.set('action', 'TEMPLATE');
  url.searchParams.set('text', event.name);

  const locationName = formatLocationNameByEvent(event);
  const details = [`${t('Event Detail')}: ${webPath(eventPath(event.uid))}`];
  if (locationName) {
    details.push(`${t('Event Location')}: ${locationName}`);
  }
  if (event.access) {
    details.push(`${t('Event Access')}: ${event.access}`);
  }
  if (event.phoneNumber) {
    details.push(`${t('Event Phone')}: ${event.phoneNumber}`);
  }
  if (event.url) {
    details.push(`${t('Event URL')}: ${event.url}`);
  }
  const detailsText = `${details.join('\n')}\n\nPowered by Jicoo`;
  url.searchParams.set('details', detailsText);
  if (event.address) {
    url.searchParams.set('location', event.address);
  }
  const format = "yyyyMMdd'T'HHmmss";
  const timeZone = event.timeZone;
  url.searchParams.set(
    'dates',
    [
      getDateTime(event.startedAt, timeZone).toFormat(format),
      getDateTime(event.endedAt, timeZone).toFormat(format),
    ].join('/'),
  );
  return url.toString();
}

function outlookCalendarLink(event: SharedEventDetailItem) {
  const t = translateInComponent(translations);
  const url = new URL('https://outlook.live.com/calendar/0/deeplink/compose');
  const timeZone = event.timeZone;
  url.searchParams.set('rru', 'addevent');
  url.searchParams.set('startdt', formatToISO(event.startedAt, timeZone));
  url.searchParams.set('enddt', formatToISO(event.endedAt, timeZone));
  url.searchParams.set('subject', event.name);

  const details = [`${t('Event Detail')}: ${webPath(eventPath(event.uid))}`];
  const locationName = formatLocationNameByEvent(event);
  if (locationName) {
    details.push(`${t('Event Location')}: ${locationName}`);
  }
  if (event.access) {
    details.push(`${t('Event Access')}: ${event.access}`);
  }
  if (event.phoneNumber) {
    details.push(`${t('Event Phone')}: ${event.phoneNumber}`);
  }
  if (event.url) {
    details.push(`${t('Event URL')}: ${event.url}`);
  }
  const detailsText = `${details.join('<br>')}<br><br>Powered by Jicoo`;
  url.searchParams.set('body', detailsText);

  if (event.address) {
    url.searchParams.set('location', event.address);
  }
  return url.toString();
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const googleCalendarUrl = googleCalendarLink(props.event);
  const outlookCalendarUrl = outlookCalendarLink(props.event);

  const onClickCalendar = async (name: string) => {
    switch (name) {
      case 'google':
        window.open(googleCalendarUrl);
        break;
      case 'outlook':
        window.open(outlookCalendarUrl);
        break;
      case 'ical':
        // window.open(props.googleCalendarUrl);
        break;
      default:
        break;
    }
    tracker.setEvent('event post calendar', {
      calendarName: name,
      locationId: props.event.locationId,
    });
  };

  return (
    <>
      <CalendarHeadline variant="h3">{t('Add to Calendar')}</CalendarHeadline>
      <CalendarButtonContainer isSignedUp={!!props.viewer}>
        <CalendarButton
          onClick={() => onClickCalendar('google')}
          color="primary"
          variant="outlined"
        >
          <Icon name="plus" />
          {t('Add to Google Calendar')}
        </CalendarButton>
        <CalendarButton
          onClick={() => onClickCalendar('outlook')}
          color="primary"
          variant="outlined"
        >
          <Icon name="plus" />
          {t('Add to Outlook')}
        </CalendarButton>
      </CalendarButtonContainer>
    </>
  );
}

const CalendarHeadline = styled(Headline)`
  margin-bottom: ${spacing(8)};
  font-size: ${fontSize(14)};
  ${mediaMax.smartphone} {
    margin-bottom: ${spacing(16)};
  }
`;
const CalendarButtonContainer = styled.div<{ isSignedUp?: boolean }>`
  display: flex;
  margin-bottom: ${({ isSignedUp }) =>
    isSignedUp ? spacing(24) : spacing(12)};
  ${mediaMax.tablet} {
    flex-direction: column;
  }
`;
const CalendarButton = styled(Button)`
  margin-right: ${spacing(16)};
  height: auto;
  padding: ${spacing(8)} ${spacing(16)};
  background: ${white};

  ${mediaMax.tablet} {
    max-width: 240px;
    margin-right: 0;
    margin-bottom: ${spacing(16)};
  }
  ${mediaMax.smartphone} {
    max-width: 100%;
  }
`;
export const Hr = styled.div`
  height: 1px;
  margin: ${spacing(32)} 0;
  background-color: ${gray05};
`;
